import {
  WorkerWindowMsgType,
  WorkerWindowRunAction,
  WorkerWindowReadyInfo,
  CreateSyncWokerProcessData,
} from '@/common/workerWindowDataDefine';
import { IpcEvents } from '@/ipc-events';
import ipc from '@/renderer/scripts/utils/ipc';
import WorkerProcessHelper from '@/renderer/scripts/utils/WorkerProcess/WorkerProcessHelper';
import WorkerProcessIPCHepler from '@/renderer/scripts/utils/WorkerProcess/WorkerProcessIPCHepler';
import ISyncServiceCommunicatorProxySide from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommunicator/ISyncServiceCommunicatorProxySide';
import { CmdFromWorker } from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommand/CmdFromWorker';
import { CmdFromRenderer } from '@/renderer/scripts/CoSync/SyncCenter/ServiceMgr/ServiceCommand/CmdFromRenderer';
import { CoSyncInfo } from '@/workerProcessRenderer/scripts/CoSyncEngine/common/CoSyncConfig';
import { IS_PRIVATE } from '@/common/constant';
import i18n from '@/locales/i18n';

/**
 *
 *
 * @export
 * @class SyncServiceIpcCommunicatorRenderSide
 * @implements {ISyncServiceCommunicatorProxySide}
 * 跨进程的同步盘服务通信器(主renderer进程侧)
 */
export default class SyncServiceIpcCommunicatorRenderSide implements ISyncServiceCommunicatorProxySide {
  private tunnelReadyResolver: (() => void) | null = null;
  private whenTunnelReady: Promise<void>;
  private workerId: number | null = null;
  private onMessageHandler: ((cmd: CmdFromWorker) => Promise<any>) | null = null;
  private onWorkerCrashHandler: (() => Promise<void>) | null = null;
  private ipcHelper: WorkerProcessIPCHepler | null = null;
  private workerHelper: WorkerProcessHelper;

  // 初始化的回调处理器
  private readonly initHandler = (info: WorkerWindowReadyInfo): void => {
    logger.ui.info('SyncServiceIpcCommunicatorRenderSide on TYPE_WORKER_WINDOW_READY');
    if (this.tunnelReadyResolver) {
      this.setUpIpcHelper(info.parentWebContentsId, info.workerWebContentsId);
      this.tunnelReadyResolver();
    }
  };

  // 消息的回调处理
  private readonly messageHandler = (e?: any, args?: any): Promise<any> => {
    logger.ui.info('SyncServiceIpcCommunicatorRenderSide', args.message);
    if (this.onMessageHandler) {
      const cmd: CmdFromWorker = JSON.parse(args.message) as CmdFromWorker;
      return this.onMessageHandler(cmd);
    }

    return Promise.resolve();
  };

  // worker进程崩溃的回调
  private readonly crashHandler = (info: WorkerWindowReadyInfo): void => {
    if (this.onWorkerCrashHandler) {
      if (this.workerId) {
        this.workerHelper.destoryWorkerProcess(this.workerId);
      }

      this.onWorkerCrashHandler();
    }
  };

  constructor(userInfo: CoFile.UserInfo, config: CoSyncInfo) {
    this.whenTunnelReady = new Promise<void>(resolve => (this.tunnelReadyResolver = resolve));
    this.workerHelper = new WorkerProcessHelper();
    this.asyncInit(userInfo, config);
  }

  /**
   * @description 异步初始化
   * @private
   * @param {CoFile.UserInfo} userInfo
   * @param {CoSyncInfo} config
   * @memberof SyncServiceIpcCommunicatorRenderSide
   */
  private async asyncInit(userInfo: CoFile.UserInfo, config: CoSyncInfo) {
    this.workerHelper.on(WorkerWindowMsgType.TYPE_WORKER_WINDOW_READY, this.initHandler);
    this.workerHelper.on(WorkerWindowMsgType.TYPE_ABEND, this.crashHandler);
    this.workerId = await this.workerHelper.creatWorkerProcess<CreateSyncWokerProcessData>(
      WorkerWindowRunAction.TYPE_SYNC,
      { userInfo, config, language: i18n.locale },
    );
  }

  private setUpIpcHelper(rendererId: number, workerId: number) {
    this.ipcHelper = new WorkerProcessIPCHepler(rendererId, workerId);
    this.ipcHelper.init();
    this.ipcHelper.handle(IpcEvents.CO_SYNC_IPC_NOTIFY, this.messageHandler);
  }

  // 反初始化
  public async deinit(): Promise<void> {
    this.ipcHelper?.offHandle(IpcEvents.CO_SYNC_IPC_NOTIFY);
    this.ipcHelper?.unInit();
    this.workerHelper.off(WorkerWindowMsgType.TYPE_ABEND, this.crashHandler);
    this.workerHelper.off(WorkerWindowMsgType.TYPE_WORKER_WINDOW_READY, this.initHandler);

    if (this.workerId) {
      await this.workerHelper.destoryWorkerProcess(this.workerId);
      this.workerId = null;
    }

    this.onMessageHandler = null;
    this.onWorkerCrashHandler = null;
    ipc.off(IpcEvents.WORKER_WINDOW_DATA_NOTIFY, this.initHandler);
  }

  // 发送消息到worker进程的同步服务
  public async send(cmd: CmdFromRenderer): Promise<any> {
    await this.whenTunnelReady;
    if (this.workerId === null) return Promise.reject({ code: 'EmptyWorkerId' });
    if (this.ipcHelper === null) return Promise.reject({ code: 'EmptyIpcHelper' });

    return this.ipcHelper.invoke(
      IpcEvents.CO_SYNC_IPC_NOTIFY,
      {
        type: WorkerWindowMsgType.TYPE_CO_SYNC_MESSAGE,
        message: JSON.stringify(cmd),
      },
      IS_PRIVATE ? 1000 * 60 : 1000 * 10, // 公有云10s超时,私有化为了POC改成60s
    );
  }

  // 设置通知处理
  public attachNotifyHandler(handler: (cmd: CmdFromWorker) => Promise<any>): void {
    this.onMessageHandler = handler;
  }

  public attachWorkerCrashHandler(handler: () => Promise<void>): void {
    this.onWorkerCrashHandler = handler;
  }
}
